import React from "react";
import PropTypes from "prop-types";
import { InlineWaiting } from "components/ui";
import MdRefresh from "react-icons/lib/md/refresh";
import MdWarning from "react-icons/lib/md/warning";
import { MdBugReport } from "react-icons/lib/md";
import HasRolePermission from "components/shared/has-role-permission";
import PureModal from "components/ui/modal/pure-modal";

const BonafideFooter = ({
  handleRefresh,
  handleCreatePreOrder,
  handleCreateOrderWithAuth,
  handleCreateOrder,
  currentStep,
  canOverrideOrderCreation,
  editAuth,
  isFetching,
  hasPendingTask,
  needsBranch,
  bonafideSelectedResult
}) => {
  const [createAuth, setCreateAuth] = React.useState(false);
  return (
    <div className="bonafide-footer">
      {isFetching ? (
        <InlineWaiting />
      ) : (
        <React.Fragment>
          <i title="Refresh" className="refresh-button" onClick={handleRefresh}>
            <MdRefresh />
          </i>
          {currentStep < 3 ? (
            <React.Fragment>
              <div
                className={`bonafide-request-order-info ${
                  currentStep === 1 ? "current" : "unavailable"
                }`}
                onClick={handleCreatePreOrder}
              >
                <span className="step-number">1</span>
                <span className="step-label">Verify Order</span>
              </div>

              <HasRolePermission
                allowedRoles={[
                  "Administrator",
                  "OfficeAdministrator",
                  "CompanyAdministrator",
                  "ServiceCoach",
                  "ServiceAdmin",
                  'ContractorAdministrator'
                ]}
              >
                <div
                  className={`bonafide-request-order-info ${
                    currentStep === 2 && !hasPendingTask && !needsBranch
                      ? "current"
                      : "unavailable"
                  }`}
                  style={{ fontWeight: editAuth ? "bold" : "normal" }}
                  onClick={() => {
                    if (editAuth) return;
                    if (createAuth) {
                      handleCreateOrderWithAuth();
                    } else handleCreateOrder();
                  }}
                >
                  <span className="step-number">2</span>
                  <span className="step-label">Send Order For Processing</span>
                </div>
              </HasRolePermission>
              {currentStep === 2 && hasPendingTask && (
                <div className="cmn-warning">
                  <MdWarning size={16} /> <span>Order has pending task</span>
                </div>
              )}
              {currentStep < 3 && !hasPendingTask && needsBranch && (
                <div className="cmn-warning">
                  <MdWarning size={16} />{" "}
                  <span>
                    Patient has no branch assigned — assign a branch before
                    sending
                  </span>
                </div>
              )}
              {canOverrideOrderCreation ||
                (currentStep == 2 && !hasPendingTask && (
                  <HasRolePermission
                    allowedRoles={[
                      "Administrator",
                      "OfficeAdministrator",
                      "CompanyAdministrator",
                      "ServiceCoach",
                      "ServiceAdmin",
                      'ContractorAdministrator'
                    ]}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={createAuth}
                        disabled={editAuth}
                        onChange={({ target }) => setCreateAuth(target.checked)}
                        style={{ margin: "0 5px" }}
                      />
                      <span>Create Auth</span>
                    </label>
                  </HasRolePermission>
                ))}
              {canOverrideOrderCreation && (
                <HasRolePermission
                  allowedRoles={[
                    "Administrator",
                    "OfficeAdministrator",
                    "CompanyAdministrator",
                    "ServiceCoach",
                    "ServiceAdmin",
                    'ContractorAdministrator'
                  ]}
                >
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      justifyContent: "flex-end"
                    }}
                    className={`bonafide-request-order-info ${
                      needsBranch ? "unavailable" : "current"
                    }`}
                    onClick={() => {
                      if (createAuth) {
                        handleCreateOrderWithAuth();
                      } else handleCreateOrder();
                    }}
                  >
                    <div className="step-label">
                      Manual Override - Send Order For Processing
                    </div>
                  </div>
                </HasRolePermission>
              )}
            </React.Fragment>
          ) : (
            <div
              className="bonafide-request-order-info unavailable"
              style={{ background: "unset" }}
            >
              Waiting For Processing
            </div>
          )}
          {currentStep === 2 && (
            <div
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "flex-end"
              }}
            >
              <div
                className="bonafide-request-order-info current"
                onClick={async () => {
                  if (isFetching) return;
                  await handleCreatePreOrder();
                }}
              >
                <span className="step-label">Re-Verify Order</span>
              </div>
            </div>
          )}
          <HasRolePermission allowedRoles={["Administrator"]}>
            <DebugWindow bonafideSelectedResult={bonafideSelectedResult} />
          </HasRolePermission>
        </React.Fragment>
      )}
    </div>
  );
};

BonafideFooter.propTypes = {
  currentStep: PropTypes.number.isRequired,
  handleCreateOrder: PropTypes.func.isRequired,
  handleCreateOrderWithAuth: PropTypes.func.isRequired,
  handleCreatePreOrder: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  canOverrideOrderCreation: PropTypes.bool,
  hasPendingTask: PropTypes.bool,
  needsBranch: PropTypes.bool,
  isFetching: PropTypes.bool,
  editAuth: PropTypes.bool,
  bonafideSelectedResult: PropTypes.object
};
export default BonafideFooter;

const DebugWindow = ({ bonafideSelectedResult }) => {
  if (!bonafideSelectedResult) return null;
  const formatJson = (str) => {
    try { return JSON.stringify(JSON.parse(str), null, 2); }
    catch (e) { return str; }
  };
  return (
    <PureModal
      renderTrigger={({ openModal }) => (
        <i title="Debug" className="refresh-button" onClick={openModal}>
          <MdBugReport />
        </i>
      )}
    >
      <div style={{ padding: 10 }}>
        <h5>Request</h5>
        <pre>{formatJson(bonafideSelectedResult.requestBody)}</pre>
        <h5>Response</h5>
        <pre>{formatJson(bonafideSelectedResult.responseBody)}</pre>
      </div>
    </PureModal>
  );
};

DebugWindow.propTypes = {
  bonafideSelectedResult: PropTypes.shape({
    requestBody: PropTypes.string,
    responseBody: PropTypes.string
  })
};
